// 검색 결과 화면.
// 작업계획서 4.2 (9 + 보완): 상품명 검색, 결과 없음 처리.
// 자동완성/인기 검색어는 후속 단계(Phase 4)에서 검토.

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { listProducts } from "@/features/catalog/catalogApi.ts";
import ProductCard from "@/features/catalog/ProductCard.tsx";

const PAGE_SIZE = 20;

// 상품 검색 화면.
export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") ?? "";
  const [q, setQ] = useState(initial);
  const appliedQ = params.get("q") ?? "";

  const query = useInfiniteQuery({
    queryKey: ["catalog", "search", appliedQ],
    queryFn: ({ pageParam }) =>
      listProducts({ q: appliedQ, cursor: pageParam as string | undefined, limit: PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: appliedQ.length > 0,
  });

  useEffect(() => {
    function onScroll() {
      if (!query.hasNextPage || query.isFetchingNextPage) return;
      const remaining = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
      if (remaining < 400) {
        void query.fetchNextPage();
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [query]);

  function submit() {
    const trimmed = q.trim();
    setParams(trimmed ? { q: trimmed } : {});
  }

  const products = query.data?.pages.flatMap((p) => p.products) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return (
    <div className="flex flex-col">
      <header className="sticky top-[var(--safe-top)] z-10 flex items-center gap-2 border-b border-ink-200 bg-white px-4 py-3">
        <Link
          to="/"
          className="shrink-0 rounded-md px-2 py-1 text-sm hover:bg-ink-100"
          aria-label="홈으로"
        >
          ← 홈
        </Link>
        {/* min-w-0: flex 자식 기본 min-width:auto 때문에 검색 영역이 콘텐츠 고유폭 아래로 줄지 않아
            좁은 화면에서 가로 넘침(가로 스크롤)이 생기던 것을 차단. */}
        <div className="flex min-w-0 flex-1 gap-2">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
            placeholder="상품명 검색"
            className="min-h-touch min-w-0 flex-1 rounded border border-ink-300 px-3 text-base"
            autoFocus
          />
          <button
            type="button"
            onClick={submit}
            className="inline-flex min-h-touch shrink-0 items-center whitespace-nowrap rounded bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700"
          >
            검색
          </button>
        </div>
      </header>
      <main className="mx-auto w-full max-w-screen-sm px-4 py-4">
        {appliedQ.length === 0 ? (
          <p className="rounded-md border border-dashed border-ink-300 p-8 text-center text-sm text-ink-600">
            찾으시는 상품명을 입력해 주세요.
          </p>
        ) : query.isLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square animate-pulse rounded-lg bg-ink-100" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-8 text-center">
            <p className="text-sm text-ink-600">
              <span className="font-semibold">{appliedQ}</span> 검색 결과가 없습니다.
            </p>
            <p className="text-xs text-ink-600">철자나 단어를 다르게 입력해 보세요.</p>
          </div>
        ) : (
          <>
            <p className="mb-3 text-sm text-ink-600">
              <span className="font-semibold text-ink-900">{appliedQ}</span> 검색 결과 {total}개
            </p>
            <div className="grid grid-cols-2 gap-3">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
            {query.isFetchingNextPage ? (
              <p className="py-4 text-center text-sm text-ink-600">불러오는 중...</p>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
